import type { ReactNode } from 'react'
import DOMPurify from 'dompurify'

import { cn } from '@/utils/cn'

// Some clinical fields (e.g. a visit's "Reason") store HTML imported from external EHR/referral
// systems — tables, lists, and basic text formatting. Render it through DOMPurify with an
// allowlist limited to those structural/formatting tags; everything else (scripts, styles, event
// handlers, links, images) is stripped, so the markup can never execute. Only structural table
// attributes are kept.
const ALLOWED_TAGS = [
  'p',
  'br',
  'span',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'colgroup',
  'col',
]

const sanitize = (html: string): string =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['colspan', 'rowspan'],
    ALLOW_DATA_ATTR: false,
  })

// The imported markup ships no styling of its own, so without this its tables collapse into each
// other (columns with no separation, headers indistinguishable from data). These scoped defaults
// give lists their markers and lay tables out legibly: a full-width grid, an underlined header row,
// and padded, top-aligned cells whose long values wrap inside their column instead of colliding.
const CONTENT_CLASSES = cn(
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
  // table-fixed: an auto-layout table can't shrink below its min-content width, so an imported
  // table nested inside a width-capped cell would force horizontal overflow; fixed layout sizes
  // it to the container and lets break-words wrap the values inside each column.
  '[&_table]:my-2 [&_table]:w-full [&_table]:table-fixed [&_table]:border-collapse',
  '[&_th]:border-b [&_th]:border-gray-300 [&_th]:px-3 [&_th]:py-2',
  '[&_th]:text-left [&_th]:font-semibold [&_th]:align-top',
  '[&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_td]:break-words',
)

type Props = {
  html: string
  className?: string
  // Rendered when sanitization yields nothing — including input that is non-empty but entirely
  // disallowed markup (e.g. `<script>…</script>`), which would otherwise show as an empty box.
  fallback?: ReactNode
}

/** Renders sanitized HTML. Use instead of a raw `dangerouslySetInnerHTML` for any stored markup. */
export const SafeHtml = ({ html, className, fallback }: Props) => {
  const clean = sanitize(html)
  if (!clean) return <>{fallback}</>
  return (
    <div className={cn(CONTENT_CLASSES, className)} dangerouslySetInnerHTML={{ __html: clean }} />
  )
}
